import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type Bookmark,
  addBookmark,
  listBookmarkAyahIds,
  listBookmarks,
  removeBookmarkById,
  removeBookmarkByAyah,
} from '@/lib/queries/bookmarks';
import { useSession } from './useSession';

/** Full bookmark rows. Disabled when anonymous. */
export function useBookmarks() {
  const session = useSession();
  return useQuery<Bookmark[], Error>({
    queryKey: ['bookmarks', session.data?.user.id ?? null],
    queryFn: listBookmarks,
    enabled: !!session.data,
  });
}

/** Just the ayah_ids — small payload, cheap to subscribe to from every AyahLine. */
export function useBookmarkedAyahIds() {
  const session = useSession();
  return useQuery<Set<number>, Error>({
    queryKey: ['bookmarks', 'ayah_ids', session.data?.user.id ?? null],
    queryFn: listBookmarkAyahIds,
    enabled: !!session.data,
  });
}

type ToggleInput = {
  ayahId: number;
  surahId: number;
  ayahNumber: number;
  /**
   * The action to perform. The CALLER is responsible for deciding this
   * from the *current* (pre-mutation) bookmark state — never read it
   * inside mutationFn, which sees the already-optimistically-updated cache.
   */
  action: 'add' | 'remove';
};

/**
 * Toggle a bookmark for an ayah. Optimistically updates the cache so the
 * UI flips instantly; rolls back on error.
 *
 * IMPORTANT: caller passes `action: 'add' | 'remove'` explicitly. An earlier
 * version of this hook decided the action by reading the cache inside
 * mutationFn — but mutationFn runs AFTER onMutate, so the cache it saw
 * was already optimistically updated, which flipped every decision.
 */
export function useToggleBookmark() {
  const queryClient = useQueryClient();
  const session = useSession();

  return useMutation({
    mutationFn: async (input: ToggleInput) => {
      const user = session.data?.user;
      if (!user) throw new Error('Not signed in');
      if (input.action === 'remove') {
        await removeBookmarkByAyah(user.id, input.ayahId);
      } else {
        await addBookmark({
          user_id: user.id,
          ayah_id: input.ayahId,
          surah_id: input.surahId,
          ayah_number: input.ayahNumber,
        });
      }
    },
    onMutate: async (input) => {
      const userId = session.data?.user.id;
      if (!userId) return;
      const key = ['bookmarks', 'ayah_ids', userId] as const;
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Set<number>>(key);
      const next = new Set(previous ?? new Set<number>());
      if (input.action === 'add') next.add(input.ayahId);
      else next.delete(input.ayahId);
      queryClient.setQueryData(key, next);
      return { previous };
    },
    onError: (_err, _input, context) => {
      const userId = session.data?.user.id;
      if (!userId || !context) return;
      queryClient.setQueryData(
        ['bookmarks', 'ayah_ids', userId],
        context.previous,
      );
    },
    onSettled: () => {
      const userId = session.data?.user.id;
      if (!userId) return;
      queryClient.invalidateQueries({ queryKey: ['bookmarks', userId] });
      queryClient.invalidateQueries({
        queryKey: ['bookmarks', 'ayah_ids', userId],
      });
    },
  });
}

export function useRemoveBookmark() {
  const queryClient = useQueryClient();
  const session = useSession();
  return useMutation({
    mutationFn: (id: string) => removeBookmarkById(id),
    onSuccess: () => {
      const userId = session.data?.user.id;
      if (!userId) return;
      queryClient.invalidateQueries({ queryKey: ['bookmarks', userId] });
      queryClient.invalidateQueries({
        queryKey: ['bookmarks', 'ayah_ids', userId],
      });
    },
  });
}
